import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { logout } from '../../store/slices/authSlice';
import { useNotifications } from '../../hooks/useNotifications';
import { useUserInfo } from '../../hooks/useUserInfo';
import NotificationMenu from './NotificationMenu';
import ProfileMenu from './ProfileMenu';
import defaultProfileImage from '../../assets/images/profile.png';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { unreadCount } = useNotifications();
  const { userInfo, loading: userInfoLoading } = useUserInfo(); // 로딩 상태도 가져오기
  const [localUserInfo, setLocalUserInfo] = useState<any>(null); // 로컬 사용자 정보 상태
  
  // 실제 사용할 사용자 정보 결정 (로컬 우선, 전역 백업)
  const effectiveUserInfo = localUserInfo || userInfo;
  
  // 디버깅용 로그 추가
  // console.log('=== Header Debug ===');
  // console.log('userInfo (global):', userInfo);
  // console.log('localUserInfo:', localUserInfo);
  // console.log('effectiveUserInfo:', effectiveUserInfo);
  // console.log('isAuthenticated:', isAuthenticated);
  // console.log('userInfoLoading:', userInfoLoading);
  // console.log('=====================');
  
  // 직접 사용자 정보를 가져오는 함수
  const loadUserInfoDirectly = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token || !isAuthenticated) return;
    
    try {
      console.log('Header - Loading user info directly...');
      const { getUserInfo } = await import('../../services/auth/authApi');
      const info = await getUserInfo();
      console.log('Header - User info loaded directly:', info);
      console.log('Header - image_url specifically:', info?.image_url);
      console.log('Header - image_url exists:', !!info?.image_url);
      console.log('Header - image_url type:', typeof info?.image_url);
      setLocalUserInfo(info);
    } catch (error) {
      console.error('Header - Failed to load user info directly:', error);
    }
  }, [isAuthenticated]);

  // 렌더링 시점 로그
  useEffect(() => {
    console.log('Header render effect - isAuthenticated:', isAuthenticated, 'userInfo:', userInfo);
  });

  // 인증 상태 변경 시 및 토큰 존재 시 사용자 정보 직접 로드
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    
    // Redux 인증 상태나 토큰 존재 여부 중 하나라도 true면 로드
    if (isAuthenticated || token) {
      console.log('Header - Authentication detected (Redux or Token), loading user info directly', {
        isAuthenticated,
        hasToken: !!token
      });
      loadUserInfoDirectly();
    } else {
      // 로그아웃 시 로컬 사용자 정보 클리어
      console.log('Header - No authentication, clearing local user info');
      setLocalUserInfo(null);
    }
  }, [isAuthenticated, loadUserInfoDirectly]);

  // 커스텀 이벤트 리스닝 (로그인 성공 시 강제 업데이트)
  useEffect(() => {
    const handleUserInfoUpdated = () => {
      console.log('Header - Received userInfoUpdated event, force loading');
      loadUserInfoDirectly();
    };

    window.addEventListener('userInfoUpdated', handleUserInfoUpdated);
    return () => {
      window.removeEventListener('userInfoUpdated', handleUserInfoUpdated);
    };
  }, [loadUserInfoDirectly]);

  // 컴포넌트 마운트 시에도 토큰 확인
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token && !effectiveUserInfo) {
      console.log('Header - Mount with token but no user info, loading directly');
      loadUserInfoDirectly();
    }
  }, []); // 마운트 시에만 실행
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [profileImageLoaded, setProfileImageLoaded] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  
  // 검색 팝업 상태 추가
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // 알림 및 프로필 메뉴 참조
  const notificationRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  
  // 검색 팝업 참조
  const searchPopupRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // 마우스 떠날 때 딜레이 추가를 위한 참조
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 프로필 이미지가 변경될 때마다 로딩 상태 리셋
  useEffect(() => {
    const effectiveUserInfo = localUserInfo || userInfo;
    console.log('Header useEffect - userInfo changed:', {
      userInfo,
      localUserInfo,
      effectiveUserInfo,
      imageUrl: effectiveUserInfo?.image_url,
      isAuthenticated
    });
    
    if (effectiveUserInfo?.image_url) {
      setProfileImageLoaded(false);
      setProfileImageError(false);
      
      // 이미지 프리로딩
      const img = new Image();
      img.onload = () => {
        console.log('Header - Image preload success:', effectiveUserInfo.image_url);
        setProfileImageLoaded(true);
      };
      img.onerror = (error) => {
        console.log('Header - Image preload failed:', effectiveUserInfo.image_url, error);
        setProfileImageError(true);
      };
      img.src = effectiveUserInfo.image_url;
    } else {
      // userInfo가 없거나 image_url이 없는 경우에도 로딩 완료로 처리
      console.log('Header - No image URL, using default');
      setProfileImageLoaded(true);
      setProfileImageError(false);
    }
  }, [userInfo?.image_url, localUserInfo?.image_url, isAuthenticated]);

  // 프로필 이미지 소스 결정
  const getProfileImageSrc = () => {
    const effectiveUserInfo = localUserInfo || userInfo;
    console.log('getProfileImageSrc - effectiveUserInfo:', effectiveUserInfo);
    console.log('getProfileImageSrc - image_url:', effectiveUserInfo?.image_url);
    console.log('getProfileImageSrc - profileImageError:', profileImageError);
    
    // 이미지 로드 오류가 있거나 사용자 정보가 없거나 이미지 URL이 없으면 기본 이미지
    if (profileImageError || !effectiveUserInfo || !effectiveUserInfo.image_url) {
      const src = defaultProfileImage;
      console.log('Profile image src (default):', src, { 
        hasUserInfo: !!effectiveUserInfo,
        imageUrl: effectiveUserInfo?.image_url, 
        profileImageError 
      });
      return src;
    }
    
    const src = effectiveUserInfo.image_url;
    console.log('Profile image src (user):', src, { 
      hasUserInfo: !!effectiveUserInfo,
      imageUrl: effectiveUserInfo?.image_url, 
      profileImageError 
    });
    return src;
  };

  // 스크롤 이벤트 감지
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // 호버한 메뉴 아이템에 따라 다른 메뉴를 비활성화하고 해당 메뉴만 활성화하는 로직
  const handleMouseEnter = (menu: string) => {
    // 이전 타이머가 있으면 취소
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setActiveDropdown(menu);
  };

  const handleMouseLeave = () => {
    // 마우스가 떠나도 바로 사라지지 않고 지연 후 사라짐
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
      setHoveredItem(null);
    }, 100); // 100ms 딜레이
  };

  // 헤더 전체에서 마우스가 떠날 때 즉시 모든 상태 리셋
  const handleHeaderMouseLeave = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setActiveDropdown(null);
    setHoveredItem(null);
  };

  // 검색 버튼 클릭 핸들러 - 팝업 열기
  const handleSearchClick = () => {
    setSearchPopupOpen(true);
    // 팝업이 열린 후 input에 포커스
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  // 검색 팝업 닫기
  const handleSearchPopupClose = () => {
    setSearchPopupOpen(false);
    setSearchQuery('');
  };

  // 검색 실행 (엔터 키 또는 검색 버튼)
  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      handleSearchPopupClose();
    }
  };

  // 검색 입력 핸들러
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // 검색 입력에서 엔터 키 처리
  const handleSearchInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    } else if (e.key === 'Escape') {
      handleSearchPopupClose();
    }
  };

  // 네비게이션 아이템 클릭 핸들러 (드롭다운이 활성화된 상태가 아닐 때만 동작)
  const handleNavItemClick = (path: string, e: React.MouseEvent) => {
    // 드롭다운이 활성화되어 있지 않을 때만 페이지 이동
    if (!activeDropdown) {
      navigate(path);
    }
  };

  // 스트리밍 새 창 열기 함수
  const handleStreamingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const width = 1440;
    const height = 810;
    
    // 가로, 세로 모두 완전 중앙 배치
    const screenWidth = window.screen.availWidth;
    const screenHeight = window.screen.availHeight;
    
    const left = Math.round((screenWidth - width) / 2);
    const top = Math.round((screenHeight - height) / 2);
    
    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      'scrollbars=yes',
      'resizable=yes',
      'menubar=no',
      'toolbar=no',
      'location=no',
      'status=no',
      'titlebar=yes'
    ].join(',');
    
    const newWindow = window.open(
      '/class/streaming',
      'streaming_window',
      features
    );
    
    if (newWindow) {
      newWindow.focus();
    }
  };

  const handleItemMouseEnter = (item: string) => {
    // 이전 타이머가 있으면 취소
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setHoveredItem(item);
  };

  const handleItemMouseLeave = () => {
    // 드롭다운 아이템에서 마우스가 떠나면 호버 상태 리셋
    setHoveredItem(null);
  };
  
  // 알림 메뉴 열기/닫기
  const toggleNotification = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNotificationOpen(!notificationOpen);
    setProfileMenuOpen(false);
  };
  
  // 프로필 메뉴 열기/닫기
  const toggleProfileMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setProfileMenuOpen(!profileMenuOpen);
    setNotificationOpen(false);
  };
  
  // 로그아웃 처리
  const handleLogout = () => {
    setProfileMenuOpen(false);
    dispatch(logout());
    navigate('/');
  };
  
  // 로그인 처리 - 한 번 클릭: 로그인 페이지 이동
  const handleLogin = () => {
    navigate('/login');
  };
  
  // 알림 메뉴나 프로필 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // 알림 메뉴 외부 클릭 처리
      if (
        notificationOpen &&
        notificationRef.current && 
        !notificationRef.current.contains(event.target as Node) &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(event.target as Node)
      ) {
        setNotificationOpen(false);
      }
      
      // 프로필 메뉴 외부 클릭 처리
      if (
        profileMenuOpen &&
        profileMenuRef.current && 
        !profileMenuRef.current.contains(event.target as Node) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(event.target as Node)
      ) {
        setProfileMenuOpen(false);
      }

      // 검색 팝업 외부 클릭 처리
      if (
        searchPopupOpen &&
        searchPopupRef.current &&
        !searchPopupRef.current.contains(event.target as Node)
      ) {
        handleSearchPopupClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationOpen, profileMenuOpen, searchPopupOpen]);

  return (
    <>
      <HeaderContainer scrolled={isScrolled} onMouseLeave={handleHeaderMouseLeave}>
        <HeaderContent>
        <LeftSection>
          <Logo onClick={() => navigate('/')}>
            <img 
              src="/images/logo-campus-new.svg"
              alt="TOBE DOCTOR CAMPUS" 
              onError={(e) => {
                console.error('Logo failed to load');
                const target = e.currentTarget;
                target.style.display = 'none';
                const logoDiv = target.parentElement;
                if (logoDiv) {
                  logoDiv.innerHTML = '<div style="color: #448181; font-weight: 600; font-size: 16px; white-space: nowrap;">TOBE DOCTOR CAMPUS</div>';
                }
              }}
            />
          </Logo>
          <Navigation className={mobileMenuOpen ? 'mobile-open' : ''} onMouseLeave={handleMouseLeave}>
            <NavItem 
              active={isActive('/class')}
              onMouseEnter={() => handleMouseEnter('classes')}
            >
              <NavLink 
                to="/class" 
                $isActive={activeDropdown === 'classes'}
                $hasActiveDropdown={activeDropdown !== null}
              >
                CLASS
              </NavLink>
              {activeDropdown === 'classes' && (
                <DropdownMenu onClick={(e) => e.stopPropagation()}>
                  <DropdownItem 
                    isHovered={hoveredItem === 'classes-ai-basic'}
                    onMouseEnter={() => handleItemMouseEnter('classes-ai-basic')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/class/category?category=ai-basic"
                      isHovered={hoveredItem === 'classes-ai-basic'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'classes-all'}
                    >인공지능 기초</DropdownLink>
                  </DropdownItem>
                  <DropdownItem 
                    isHovered={hoveredItem === 'classes-data-analysis'}
                    onMouseEnter={() => handleItemMouseEnter('classes-data-analysis')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/class/category?category=data-analysis"
                      isHovered={hoveredItem === 'classes-data-analysis'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'classes-all'}
                    >데이터 분석</DropdownLink>
                  </DropdownItem>
                  <DropdownItem 
                    isHovered={hoveredItem === 'classes-medical-ai'}
                    onMouseEnter={() => handleItemMouseEnter('classes-medical-ai')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/class/category?category=medical-ai"
                      isHovered={hoveredItem === 'classes-medical-ai'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'classes-all'}
                    >의료 AI</DropdownLink>
                  </DropdownItem>
                  <DropdownItem 
                    isHovered={hoveredItem === 'classes-coding-environment'}
                    onMouseEnter={() => handleItemMouseEnter('classes-coding-environment')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/class/category?category=coding-environment"
                      isHovered={hoveredItem === 'classes-coding-environment'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'classes-all'}
                    >실습 환경 · 코드</DropdownLink>
                  </DropdownItem>
                  <DropdownItem 
                    isHovered={hoveredItem === 'classes-digital-healthcare'}
                    onMouseEnter={() => handleItemMouseEnter('classes-digital-healthcare')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/class/category?category=digital-healthcare"
                      isHovered={hoveredItem === 'classes-digital-healthcare'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'classes-all'}
                    >디지털 헬스케어</DropdownLink>
                  </DropdownItem>
                  <DropdownItem 
                    isHovered={hoveredItem === 'classes-future-doctor'}
                    onMouseEnter={() => handleItemMouseEnter('classes-future-doctor')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/class/category?category=future-doctor"
                      isHovered={hoveredItem === 'classes-future-doctor'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'classes-all'}
                    >미래 의료인의 역할</DropdownLink>
                  </DropdownItem>
                  <DropdownSeparator />
                  <DropdownItem 
                    isHovered={hoveredItem === 'classes-all'}
                    onMouseEnter={() => handleItemMouseEnter('classes-all')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/class/category"
                      isHovered={hoveredItem === 'classes-all'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'classes-all'}
                      isSpecial={true}
                    >클래스 전체 보기</DropdownLink>
                  </DropdownItem>
                </DropdownMenu>
              )}
            </NavItem>
            <NavItem 
              active={isActive('/coffee-chat')}
              onMouseEnter={() => handleMouseEnter('coffee-chat')}
            >
              <NavLink 
                to="/coffee-chat" 
                $isActive={activeDropdown === 'coffee-chat'}
                $hasActiveDropdown={activeDropdown !== null}
              >
                COFFEE CHAT
              </NavLink>
              {/* {activeDropdown === 'coffee-chat' && (
                <DropdownMenu>
                  <DropdownItem 
                    isHovered={hoveredItem === 'coffee-1'}
                    onMouseEnter={() => handleItemMouseEnter('coffee-1')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/coffee-chat/all"
                      isHovered={hoveredItem === 'coffee-1'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'coffee-all'}
                    >메뉴 하나</DropdownLink>
                  </DropdownItem>
                  <DropdownItem 
                    isHovered={hoveredItem === 'coffee-2'}
                    onMouseEnter={() => handleItemMouseEnter('coffee-2')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/coffee-chat/new"
                      isHovered={hoveredItem === 'coffee-2'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'coffee-all'}
                    >메뉴 둘</DropdownLink>
                  </DropdownItem>
                  <DropdownItem 
                    isHovered={hoveredItem === 'coffee-3'}
                    onMouseEnter={() => handleItemMouseEnter('coffee-3')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/coffee-chat/popular"
                      isHovered={hoveredItem === 'coffee-3'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'coffee-all'}
                    >메뉴 셋</DropdownLink>
                  </DropdownItem>
                  <DropdownSeparator />
                  <DropdownItem 
                    isHovered={hoveredItem === 'coffee-all'}
                    onMouseEnter={() => handleItemMouseEnter('coffee-all')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/coffee-chat"
                      isHovered={hoveredItem === 'coffee-all'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'coffee-all'}
                      isSpecial={true}
                    >커피챗 전체 보기</DropdownLink>
                  </DropdownItem>
                </DropdownMenu>
              )} */}
            </NavItem>
            <NavItem 
              active={isActive('/archive')}
              onMouseEnter={() => handleMouseEnter('archive')}
            >
              <NavLink 
                to="/archive" 
                $isActive={activeDropdown === 'archive'}
                $hasActiveDropdown={activeDropdown !== null}
              >
                ARCHIVE
              </NavLink>
              {/* {activeDropdown === 'archive' && (
                <DropdownMenu>
                  <DropdownItem 
                    isHovered={hoveredItem === 'archive-1'}
                    onMouseEnter={() => handleItemMouseEnter('archive-1')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/archive/articles"
                      isHovered={hoveredItem === 'archive-1'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'archive-all'}
                    >메뉴 하나</DropdownLink>
                  </DropdownItem>
                  <DropdownItem 
                    isHovered={hoveredItem === 'archive-2'}
                    onMouseEnter={() => handleItemMouseEnter('archive-2')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/archive/videos"
                      isHovered={hoveredItem === 'archive-2'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'archive-all'}
                    >메뉴 둘</DropdownLink>
                  </DropdownItem>
                  <DropdownItem 
                    isHovered={hoveredItem === 'archive-3'}
                    onMouseEnter={() => handleItemMouseEnter('archive-3')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/archive/podcasts"
                      isHovered={hoveredItem === 'archive-3'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'archive-all'}
                    >메뉴 셋</DropdownLink>
                  </DropdownItem>
                  <DropdownSeparator />
                  <DropdownItem 
                    isHovered={hoveredItem === 'archive-all'}
                    onMouseEnter={() => handleItemMouseEnter('archive-all')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/archive"
                      isHovered={hoveredItem === 'archive-all'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'archive-all'}
                      isSpecial={true}
                    >아카이브 전체 보기</DropdownLink>
                  </DropdownItem>
                </DropdownMenu>
              )} */}
            </NavItem>
            <NavItem 
              active={isActive('/community')}
              onMouseEnter={() => handleMouseEnter('community')}
            >
              <NavLink 
                to="/community" 
                $isActive={activeDropdown === 'community'}
                $hasActiveDropdown={activeDropdown !== null}
              >
                COMMUNITY
              </NavLink>
              {/* {activeDropdown === 'community' && (
                <DropdownMenu>
                  <DropdownItem 
                    isHovered={hoveredItem === 'community-1'}
                    onMouseEnter={() => handleItemMouseEnter('community-1')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/community/events"
                      isHovered={hoveredItem === 'community-1'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'community-all'}
                    >메뉴 하나</DropdownLink>
                  </DropdownItem>
                  <DropdownItem 
                    isHovered={hoveredItem === 'community-2'}
                    onMouseEnter={() => handleItemMouseEnter('community-2')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/community/mentoring"
                      isHovered={hoveredItem === 'community-2'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'community-all'}
                    >메뉴 둘</DropdownLink>
                  </DropdownItem>
                  <DropdownItem 
                    isHovered={hoveredItem === 'community-3'}
                    onMouseEnter={() => handleItemMouseEnter('community-3')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/community/forum"
                      isHovered={hoveredItem === 'community-3'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'community-all'}
                    >메뉴 셋</DropdownLink>
                  </DropdownItem>
                  <DropdownSeparator />
                  <DropdownItem 
                    isHovered={hoveredItem === 'community-all'}
                    onMouseEnter={() => handleItemMouseEnter('community-all')}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    <DropdownLink 
                      to="/community"
                      isHovered={hoveredItem === 'community-all'}
                      hasHoveredItem={hoveredItem !== null}
                      hoveredItemIsSpecial={hoveredItem === 'community-all'}
                      isSpecial={true}
                    >커뮤니티 전체 보기</DropdownLink>
                  </DropdownItem>
                </DropdownMenu>
              )} */}
            </NavItem>
            <MobileCloseButton onClick={toggleMobileMenu}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </MobileCloseButton>
          </Navigation>
        </LeftSection>

        <RightSection>
          <SearchButton $isAuthenticated={isAuthenticated} onClick={handleSearchClick}>
            <img src="/images/header/search.svg" alt="Search" width="24" height="24" />
          </SearchButton>
          
          {/* 디버깅을 위한 로그는 useEffect에서 출력하도록 수정 */}
          
          {isAuthenticated ? (
            <AuthenticatedSection>
              <NotificationButtonWrapper>
                <NotificationButton 
                  ref={notificationButtonRef}
                  onClick={toggleNotification}
                  isActive={notificationOpen}
                >
                  <img src="/images/alarm.svg" alt="Notification" width="24" height="24" />
                  {/* 읽지 않은 알림이 있을 경우 뱃지 표시 */}
                  {unreadCount > 0 && (
                    <NotificationBadge>
                      {/* {unreadCount > 99 ? '99+' : unreadCount} */}
                    </NotificationBadge>
                  )}
                </NotificationButton>
                <NotificationMenu 
                  ref={notificationRef}
                  isOpen={notificationOpen} 
                  onClose={() => setNotificationOpen(false)} 
                />
              </NotificationButtonWrapper>
              
              <ProfileButtonWrapper>
                <ProfileButton 
                  ref={profileButtonRef}
                  onClick={toggleProfileMenu}
                  isActive={profileMenuOpen}
                >
                  <ProfileImageContainer>
                    <img 
                      src={getProfileImageSrc()} 
                      alt="Profile" 
                      style={{ 
                        opacity: userInfoLoading ? 0.7 : 1, // 로딩 중일 때 반투명
                        transition: 'opacity 0.2s ease'
                      }}
                      onLoad={(e) => {
                        console.log('Header - Image loaded successfully:', e.currentTarget.src);
                        setProfileImageLoaded(true);
                      }}
                      onError={(e) => {
                        console.log('Header - Image load error:', e.currentTarget.src);
                        setProfileImageError(true);
                        const target = e.target as HTMLImageElement;
                        target.src = defaultProfileImage;
                      }}
                    />
                  </ProfileImageContainer>
                </ProfileButton>
                <ProfileMenu 
                  ref={profileMenuRef}
                  isOpen={profileMenuOpen} 
                  onClose={() => setProfileMenuOpen(false)} 
                  onLogout={handleLogout} 
                />
              </ProfileButtonWrapper>
            </AuthenticatedSection>
          ) : (
            <UnauthenticatedSection>
              <LoginButton onClick={handleLogin}>
                로그인
              </LoginButton>
              <SignupButton onClick={() => navigate('/signup')}>회원가입</SignupButton>
            </UnauthenticatedSection>
          )}
          <MobileMenuButton onClick={toggleMobileMenu}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12H21M3 6H21M3 18H21" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </MobileMenuButton>
        </RightSection>
      </HeaderContent>
    </HeaderContainer>
    
    <SearchPopup
      isOpen={searchPopupOpen}
      onClose={handleSearchPopupClose}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      onSearchSubmit={handleSearchSubmit}
      searchInputRef={searchInputRef}
      searchPopupRef={searchPopupRef}
    />
  </>
  );
};

// 검색 팝업 컴포넌트 (별도로 분리)
const SearchPopup: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onSearchSubmit: () => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  searchPopupRef: React.RefObject<HTMLDivElement>;
}> = ({ isOpen, onClose, searchQuery, onSearchQueryChange, onSearchSubmit, searchInputRef, searchPopupRef }) => {
  const handleSearchInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearchSubmit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <SearchPopupOverlay>
      <SearchPopupContainer ref={searchPopupRef}>
        <SearchPopupHeader>
          <SearchPopupCloseButton onClick={onClose}>
            <img src="/images/close-icon.svg" alt="Close" width="32" height="32" />
          </SearchPopupCloseButton>
        </SearchPopupHeader>
        <SearchInputContainer>
          <SearchInputWrapper>
            <SearchInputContent>
              <SearchIcon>
                <img src="/images/search-icon.svg" alt="Search" width="20" height="20" />
              </SearchIcon>
              <SearchInputField>
                <SearchInput
                  ref={searchInputRef}
                  type="text"
                  placeholder="검색어를 입력해주세요."
                  value={searchQuery}
                  onChange={(e) => onSearchQueryChange(e.target.value)}
                  onKeyDown={handleSearchInputKeyDown}
                />
              </SearchInputField>
            </SearchInputContent>
          </SearchInputWrapper>
        </SearchInputContainer>
      </SearchPopupContainer>
    </SearchPopupOverlay>,
    document.body
  );
};

const HeaderContainer = styled.header.withConfig({
  shouldForwardProp: (prop) => !['scrolled'].includes(prop),
})<{ scrolled: boolean }>`
  background-color: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.88)'};
  backdrop-filter: blur(64px);
  border-bottom: 1px solid rgba(112, 115, 124, 0.16);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1001;
  transition: all 0.3s ease;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: ${({ theme }) => theme.layout.containerWidth};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.layout.containerPadding};
  height: 80px;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    padding: 0 ${({ theme }) => theme.layout.containerPaddingTablet};
  }
  
  @media (max-width: 768px) {
    padding: 0 ${({ theme }) => theme.layout.containerPaddingMobile};
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 84px;
  
  @media (max-width: 768px) {
    gap: 32px;
  }
`;

const Logo = styled.div`
  cursor: pointer;
  img {
    height: 28px;
    width: auto;
    display: block;
  }
  
  /* 로고가 안 보이는 경우를 대비한 디버깅 스타일 */
  min-height: 28px;
  min-width: 134px;
  background-color: transparent;
  
  /* 이미지 로딩 실패 시 텍스트 표시 */
  &::after {
    content: '';
    display: ${props => props.theme ? 'none' : 'block'};
  }
`;

const Navigation = styled.nav`
  display: flex;
  gap: 40px; /* 원래 간격 복원 */
  
  @media (max-width: 768px) {
    display: none;
    
    &.mobile-open {
      display: flex;
      flex-direction: column;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
      background-color: white;
      z-index: 1100;
      padding: 80px 20px 20px;
      gap: 16px;
    }
  }
`;

const NavItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !['active'].includes(prop),
})<{ active: boolean }>`
  position: relative;
  padding: 8px 0;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  
  @media (max-width: 768px) {
    padding: 12px 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    width: 100%;
  }
`;

const NavLink = styled(Link)<{ $isActive?: boolean; $hasActiveDropdown?: boolean }>`
  /* 원래 텍스트 스타일은 그대로 유지 */
  color: var(--Label-Normal, #171719);
  text-decoration: none;
  transition: color 0.1s ease;
  font-weight: 600;
  font-family: 'Pretendard JP', sans-serif;
  font-size: 15px;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  text-align: center;
  text-transform: uppercase;
  
  /* 클릭 영역 확장을 위한 invisible padding */
  position: relative;
  
  /* 가상 요소로 클릭 영역만 확장 */
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    bottom: -20px;
    left: -30px;
    right: -30px;
    /* 디버그용: background: rgba(255, 0, 0, 0.1); */
  }
  
  /* 드롭다운이 활성화되고 다른 메뉴가 호버된 경우에만 회색으로 변경 */
  ${({ $isActive, $hasActiveDropdown }) => 
    $hasActiveDropdown && !$isActive && css`
      color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
    `
  }
  
  /* 호버 시에는 항상 검정색 */
  &:hover {
    color: var(--Label-Normal, #171719) !important;
  }
  
  @media (max-width: 768px) {
    &::before {
      display: none;
    }
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0; /* 텍스트 앞에 정렬 */
  transform: translateX(-30px); /* 너비가 넘어져서 왼쪽으로 이동, 정확한 값으로 조정 */
  width: 304px; /* 피그마 디자인 스펙에 맞게 수정 */
  background: var(--Background-Elevated-Normal, white);
  border-radius: 18px;
  box-shadow: 0px 0px 10px 1px rgba(0, 0, 0, 0.03);
  padding: 19px 32px; /* 피그마 디자인 스펙에 맞게 수정 */
  z-index: 10;
  margin-top: 4px; /* 드롭다운을 삼숙 더 아래로 이동 */
  outline: 1px rgba(112, 115, 124, 0.08) solid;
  outline-offset: -1px;
  display: flex; /* 피그마 스펙에 맞게 flex 추가 */
  flex-direction: column; /* 피그마 스펙에 맞게 수직 배치 */
  align-items: flex-start; /* 피그마 스펙에 맞게 왼쪽 정렬 */
  overflow: hidden;
  
  /* 드롭다운과 네비게이션 아이템 사이의 공간 메움 */
  &:before {
    content: '';
    position: absolute;
    top: -4px; /* 피그마 디자인에 맞게 조정 */
    left: 0;
    width: 100%;
    height: 4px; /* 피그마 디자인에 맞게 조정 */
  };
  
  /* 드롭다운에 마우스 진입 시 타이머 취소를 위한 건 */
  &:hover {
    pointer-events: auto;
  }
  
  @media (max-width: 768px) {
    position: static;
    transform: none; /* 모바일에서는 중앙 정렬 제거 */
    left: auto;
    width: 100%;
    box-shadow: none;
    padding: 8px 0 8px 16px;
    border-radius: 0;
    margin-top: 0;
    
    &:before {
      display: none;
    }
  }
`;

const DropdownItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isHovered'].includes(prop),
})<{ isHovered?: boolean }>`
  width: 100%; /* 너비 전체 차지 */
  display: flex;
  align-items: center;
  justify-content: flex-start;
  
  @media (max-width: 768px) {
    /* 모바일에서는 원래 패딩 유지 */
  }
`;

const DropdownSeparator = styled.div`
  align-self: stretch;
  padding: 8px 0;
  
  &:after {
    content: '';
    display: block;
    height: 1px;
    background: rgba(112, 115, 124, 0.08);
    width: 100%;
  }
`;

const DropdownLink = styled(Link).withConfig({
  shouldForwardProp: (prop) => !['isHovered', 'hasHoveredItem', 'isSpecial', 'hoveredItemIsSpecial'].includes(prop),
})<{ isHovered?: boolean; hasHoveredItem?: boolean; isSpecial?: boolean; hoveredItemIsSpecial?: boolean }>`
  /* DropdownItem 전체를 덮도록 패딩 추가 */
  padding: 11px 32px;
  margin: 0 -32px; /* 좌우 마진으로 전체 너비 확장 */
  display: block;
  width: calc(100% + 64px); /* 좌우 패딩만큼 너비 확장 */
  
  /* 기본 상태는 검정색 */
  color: var(--Label-Normal, #171719);
  text-decoration: none;
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  transition: color 0.1s ease;
  word-wrap: break-word;
  
  /* "전체 보기" 항목은 녹색 */
  ${({ isSpecial }) => 
    isSpecial && css`
      color: var(--Primary-Strong, #296768);
    `
  }
  
  /* 호버 로직:
   * 1. 일반 항목이 호버된 경우: 호버되지 않은 일반 항목들은 회색
   * 2. 특수 항목(전체보기)이 호버된 경우: 모든 일반 항목들은 검정색 유지
   */
  ${({ isHovered, hasHoveredItem, isSpecial, hoveredItemIsSpecial }) => {
    if (hasHoveredItem && !isHovered && !isSpecial && !hoveredItemIsSpecial) {
      return css`
        color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
      `;
    }
  }}
  
  @media (max-width: 768px) {
    padding: 8px 0;
    margin: 0;
    width: 100%;
  }
`;

const StreamingLink = styled.a.withConfig({
  shouldForwardProp: (prop) => !['isHovered', 'hasHoveredItem', 'isSpecial', 'hoveredItemIsSpecial'].includes(prop),
})<{ isHovered?: boolean; hasHoveredItem?: boolean; isSpecial?: boolean; hoveredItemIsSpecial?: boolean }>`
  flex: 1 1 0;
  /* 기본 상태는 검정색 */
  color: var(--Label-Normal, #171719);
  text-decoration: none;
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  display: block;
  transition: color 0.1s ease;
  word-wrap: break-word;
  cursor: pointer;
  
  /* "전체 보기" 항목은 녹색 */
  ${({ isSpecial }) => 
    isSpecial && css`
      color: var(--Primary-Strong, #296768);
    `
  }
  
  /* 호버 로직:
   * 1. 일반 항목이 호버된 경우: 호버되지 않은 일반 항목들은 회색
   * 2. 특수 항목(전체보기)이 호버된 경우: 모든 일반 항목들은 검정색 유지
   */
  ${({ isHovered, hasHoveredItem, isSpecial, hoveredItemIsSpecial }) => {
    if (hasHoveredItem && !isHovered && !isSpecial && !hoveredItemIsSpecial) {
      return css`
        color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
      `;
    }
  }}
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AuthenticatedSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: 8px; /* Search와의 추가 간격으로 총 16px 만들기 */
`;

const UnauthenticatedSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchButton = styled.button<{ $isAuthenticated: boolean }>`
  background: none;
  border: none;
  padding: 0;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  img {
    width: 24px;
    height: 24px;
  }
  
  /* 로그인 상태에서만 호버 효과 */
  ${props => props.$isAuthenticated && `
    /* 호버 시 배경 효과 */
    &:after {
      content: '';
      position: absolute;
      width: 40px;
      height: 40px;
      border-radius: 1000px;
      background-color: var(--Label-Normal, #171719);
      opacity: 0;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: -1;
      transition: opacity 0.2s ease;
    }
    
    &:hover:after {
      opacity: 0.04;
    }
    
    &:active:after {
      opacity: 0.09;
    }
  `}
`;

const NotificationButtonWrapper = styled.div`
  position: relative;
  height: 24px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NotificationButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['isActive'].includes(prop),
})<{ isActive?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-direction: column;
  flex: 1 1 0;
  
  svg {
    width: 24px;
    height: 24px;
  }
  
  /* 호버 시 배경 효과 */
  &:after {
    content: '';
    position: absolute;
    width: 40px;
    height: 40px;
    border-radius: 1000px;
    background-color: var(--Label-Normal, #171719);
    opacity: 0;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: -1;
    transition: opacity 0.2s ease;
  }
  
  &:hover:after {
    opacity: 0.04;
  }
  
  &:active:after {
    opacity: 0.09;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  min-width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: #448181;
  color: white;
  font-size: 10px;
  font-weight: 600;
  font-family: 'Pretendard JP', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  top: -2px;
  right: -2px;
  line-height: 1;
  
  /* 숫자가 없으면 작은 점으로 표시 */
  &:empty {
    padding: 0;
    min-width: 4px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    top: 0;
    right: 0;
  }
`;

const ProfileButtonWrapper = styled.div`
  position: relative;
  height: 24px;
  border-radius: 10000px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ProfileButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['isActive'].includes(prop),
})<{ isActive?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--Static-White, white);
  outline: 1px var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08)) solid;
  outline-offset: -1px;
  position: relative;
  
  /* 호버 시 배경 효과 */
  &:after {
    content: '';
    position: absolute;
    width: 40px;
    height: 40px;
    border-radius: 1000px;
    background-color: var(--Label-Normal, #171719);
    opacity: 0;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: -1;
    transition: opacity 0.2s ease;
  }
  
  &:hover:after {
    opacity: 0.04;
  }
  
  &:active:after {
    opacity: 0.09;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ProfileImageContainer = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const LoginButton = styled.button`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  background: none;
  border: 1px solid rgba(112, 115, 124, 0.25);
  border-radius: 8px;
  padding: 7px 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(112, 115, 124, 0.05);
    cursor: pointer;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SignupButton = styled.button`
  font-size: 14px;
  font-weight: 500;
  color: white;
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    cursor: pointer;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileCloseButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  position: absolute;
  top: 20px;
  right: 20px;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

/* 검색 팝업 스타일 */
const SearchPopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 100px;
  
  @media (max-width: 768px) {
    padding-top: 60px;
  }
`;

const SearchPopupContainer = styled.div`
  width: 600px;
  max-width: 90vw;
  background: var(--Background-Normal-Normal, white);
  border-radius: 12px;
  overflow: hidden;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const SearchPopupHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 32px;
`;

const SearchPopupCloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  position: relative;
  
  &:hover {
    &::before {
      content: '';
      position: absolute;
      top: -8px;
      left: -8px;
      right: -8px;
      bottom: -8px;
      background: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
      opacity: 0.04;
      border-radius: 1000px;
    }
  }
`;

const SearchInputContainer = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
`;

const SearchInputWrapper = styled.div`
  width: 100%;
  height: 64px;
  padding: 12px 20px;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: 1px var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22)) solid;
  outline-offset: -1px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
`;

const SearchIcon = styled.div`
  height: 20px;
  padding: 0 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const SearchInputContent = styled.div`
  flex: 1;
  min-height: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchInputField = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  
  &::placeholder {
    color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
  }
  
  &:focus {
    outline: none;
  }
`;

export default Header;